// src/App.js
// --- THIS IS THE CORRECTED FILE ---

import React, { useState, useEffect, useRef, useCallback } from 'react';
// --- ROUTER IMPORTS REMOVED ---
import { httpsCallable } from 'firebase/functions';
import {
  collection, query, orderBy, onSnapshot, deleteDoc, doc, getDocs, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { Loader2, Edit3, AlertTriangle, LogOut, FileText, BarChart3, Share2, BrainCircuit, Layers, RefreshCw, Sparkles, PenLine } from 'lucide-react';

// Firebase & Analytics
import { db, analytics, functionsInstance } from './firebase/firebase';

// Hooks
import { useAuth } from './hooks/useAuth';

// Layout Components
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';

// UI & Modal Components
import AuthModal from './components/auth/AuthModal';
import ConfirmationModal from './components/ui/ConfirmationModal';
import Disclaimer from './components/ui/Disclaimer';
import BulkImportModal from './components/features/BulkImportModal';
import NotificationManager from './components/ui/NotificationManager';
// --- RESETPASSWORD IMPORT REMOVED (doesn't belong here) ---

// Feature Components
import AddTptProductForm from './components/features/AddTptProductForm';
import RewriteDescriptionForm from './components/features/RewriteDescriptionForm';
import TitleGenerator from './components/features/TitleGenerator';
import NewDescriptionForm from './components/features/NewDescriptionForm';
import GeneratedDescription from './components/features/GeneratedDescription';
import SeoRankTracker from './components/features/SeoRankTracker';
import SocialMediaPostGenerator from './components/features/SocialMediaPostGenerator';
import ProductImprovementHub from './components/features/ProductImprovementHub';
import KeywordResearchTool from './components/features/KeywordResearchTool';

// --- NEW: Walkthrough Tour Component
import WalkthroughTour from './components/features/WalkthroughTour';

// --- NEW: Bundles (Beta)
import { BundleComposer } from './components/bundles';

// Audit drawer provider
import AuditDrawerProvider from './components/audit/AuditDrawerProvider';

// Audit util
import { runAudit } from './utils/audit';

// Constants
import { TPT_PURCHASE_LINK, SUPPORT_EMAIL } from './config/constants';

const ENABLE_IMPROVEMENT_HUB = true;
const ENABLE_KEYWORD_RESEARCH = true;

const Toast = ({ message, show }) => {
  if (!show) return null;
  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 bg-slate-800 text-white py-2 px-5 rounded-lg shadow-lg z-50 animate-fade-in-out">
      {message}
    </div>
  );
};

// SEO summaries (no audit here)
const loadSeoSummariesForProducts = async (uid, products) => {
  if (!uid || !Array.isArray(products) || products.length === 0) return [];
  const tasks = products.map(async (p) => {
    try {
      const ksSnap = await getDocs(
        collection(db, 'users', uid, 'products', p.id, 'keywordSummaries')
      );
      const keywordSummaries = [];
      let lastCheckedTs = null;
      let tracked = 0, improved = 0, worsened = 0, same = 0;
      let top20 = 0, top10 = 0, top3 = 0, newTop20 = 0;
      let bestDelta = -Infinity, bestKeyword = null;
      let sumDelta = 0, deltaCount = 0;
      const improvingList = [];
      ksSnap.forEach((docSnap) => {
        const d = docSnap.data() || {};
        keywordSummaries.push({ id: docSnap.id, ...d });
        const ts = d?.lastChecked?.toDate?.() || null;
        if (ts && (!lastCheckedTs || ts > lastCheckedTs)) lastCheckedTs = ts;
        const curr = Number.isFinite(d?.currentRank) ? d.currentRank : null;
        const prev = Number.isFinite(d?.previousRank) ? d.previousRank : null;
        if (curr != null) {
          tracked++;
          if (curr <= 20) top20++;
          if (curr <= 10) top10++;
          if (curr <= 3) top3++;
        }
        if (curr != null && prev != null) {
          const delta = prev - curr;
          if (delta > 0) {
            improved++;
            improvingList.push({
              phrase: d.keyword || d.phrase || docSnap.id,
              delta, curr, prev,
            });
            if (prev > 20 && curr <= 20) newTop20++;
          } else if (delta < 0) {
            worsened++;
          } else {
            same++;
          }
          if (delta > bestDelta) {
            bestDelta = delta;
            bestKeyword = d.keyword || d.phrase || docSnap.id;
          }
          sumDelta += delta;
          deltaCount++;
        }
      });
      const avgRankChange = deltaCount ? Math.round((sumDelta / deltaCount) * 10) / 10 : 0;

      return {
        ...p,
        auditOverrides: p.auditOverrides || {},
        lastRankCheck: lastCheckedTs || null,
        avgRankChange,
        keywordSummaries,
        seoKpis: {
          tracked, improved, worsened, same, top20, top10, top3, newTop20,
          bestMover: bestKeyword,
          improvingList: improvingList.sort((a, b) => b.delta - a.delta).slice(0, 5),
        },
      };
    } catch (e) {
      console.error('Error loading keyword summaries for product', p?.id, e);
      return {
        ...p,
        auditOverrides: p.auditOverrides || {},
        lastRankCheck: null,
        avgRankChange: 0,
        keywordSummaries: [],
        seoKpis: {
          tracked: 0, improved: 0, worsened: 0, same: 0, top20: 0, top10: 0, top3: 0, newTop20: 0,
          bestMover: null, improvingList: [],
        },
      };
    }
  });
  return Promise.all(tasks);
};

// --- Your existing AppContent component ---
const AppContent = ({ authHook }) => {
  const { currentUser, userAccessData, isAuthLoading, handleLogout, triggerAuthModal, handleGoogleSignIn } = authHook;

  // --- STATE FOR POPUP MANAGEMENT ---
  const [isSystemNotificationOpen, setIsSystemNotificationOpen] = useState(false);

  const [activeTab, setActiveTab] = useState('descriptionTools');
  const [descSubTab, setDescSubTab] = useState('existing'); // 'existing' | 'titles' | 'new'
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userTptProducts, setUserTptProducts] = useState([]);
  const [savedDescriptions, setSavedDescriptions] = useState([]);
  const [showRewriteSection, setShowRewriteSection] = useState(false);
  const [selectedTptProductForRewrite, setSelectedTptProductForRewrite] = useState(null);
  const [generatedDescription, setGeneratedDescription] = useState('');
  const [formSnapshot, setFormSnapshot] = useState({});
  const [snapshotToLoad, setSnapshotToLoad] = useState(null);
  const [productName, setProductName] = useState('');
  const [selectedTitle, setSelectedTitle] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [onConfirmAction, setOnConfirmAction] = useState(() => () => {});
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [showSyncToast, setShowSyncToast] = useState(false);
  const [hubProducts, setHubProducts] = useState([]);
  const [isReanalyzingAll, setIsReanalyzingAll] = useState(false);
  const [preselectedSeoProductId, setPreselectedSeoProductId] = useState(null);
  const [preselectedSocialProduct, setPreselectedSocialProduct] = useState(null);
  const [preselectedKeywordForGraph, setPreselectedKeywordForGraph] = useState(null);
  const [preselectedCoachProductId, setPreselectedCoachProductId] = useState(null);
  const mainContentRef = useRef(null);
  const initialAuthCheckPerformed = useRef(false);

  // --- NEW: bundle generator wrapper (use your existing CF name if different)
  const generateBundleCopy = useCallback(async ({ systemPrompt, userPrompt }) => {
    // If you already have a callable used by NewDescriptionForm, you can reuse that here.
    // Replace 'ai_generate_generic' with your actual generator function name if needed.
    const fn = httpsCallable(functionsInstance, 'ai_generate_generic');
    const res = await fn({ systemPrompt, userPrompt });
    return res?.data?.text || '';
  }, []);

  // All your useEffect hooks and handler functions (unchanged)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const adSource = params.get('utm_source');
      const adCampaign = params.get('utm_campaign');
      if (adSource) sessionStorage.setItem('ad_source', adSource);
      if (adCampaign) sessionStorage.setItem('ad_campaign', adCampaign);
    } catch (error) {
      console.error('Error capturing UTM params:', error);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && !document.getElementById('google-gtag-script')) {
      const gtagScript = document.createElement('script');
      gtagScript.id = 'google-gtag-script';
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=AW-17181144542`;
      document.head.appendChild(gtagScript);

      const gtagInitScript = document.createElement('script');
      gtagInitScript.id = 'google-gtag-init-script';
      gtagInitScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-17181144542');
      `;
      document.head.appendChild(gtagInitScript);
    }
  }, []);

  useEffect(() => {
    if (!isAuthLoading && !initialAuthCheckPerformed.current) {
      initialAuthCheckPerformed.current = true;
      if (!currentUser) {
        triggerAuthModal('Sign up for a free trial to get started!');
      }
    }
  }, [isAuthLoading, currentUser, triggerAuthModal]);

  useEffect(() => {
    if (!currentUser || !userAccessData) return;
    const triggerUpdate = async () => {
      const twentyFourHours = 24 * 60 * 60 * 1000;
      const lastUpdate = userAccessData.lastAutoUpdateAt?.toDate().getTime() || 0;
      if (Date.now() - lastUpdate > twentyFourHours) {
        setShowSyncToast(true);
        try {
          const triggerOnLoginUpdate = httpsCallable(functionsInstance, 'triggerOnLoginUpdate');
          await triggerOnLoginUpdate();
        } catch (error) {
          console.error('Error syncing ranks on login:', error);
        } finally {
          setTimeout(() => setShowSyncToast(false), 3000);
        }
      }
    };
    triggerUpdate();
  }, [currentUser, userAccessData]);

  // userProducts listener
  useEffect(() => {
    if (!currentUser) {
      setUserTptProducts([]);
      return;
    }
    const q = query(collection(db, 'userProducts', currentUser.uid, 'products'), orderBy('addedAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const products = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setUserTptProducts(products);
      },
      (err) => console.error('Error fetching TPT products:', err)
    );
    return () => unsubscribe();
  }, [currentUser]);

  // savedDescriptions listener
  useEffect(() => {
    if (!currentUser) {
      setSavedDescriptions([]);
      return;
    }
    const q = query(collection(db, 'users', currentUser.uid, 'savedDescriptions'), orderBy('savedAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const descriptions = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setSavedDescriptions(descriptions);
      },
      (err) => console.error('Error fetching saved descriptions:', err)
    );
    return () => unsubscribe();
  }, [currentUser]);

  // hydrate hubProducts with SEO
  useEffect(() => {
    const hydrate = async () => {
      const products = userTptProducts || [];
      if (!products.length) {
        setHubProducts([]);
        return;
      }
      if (!currentUser) {
        const demo = products.map(p => ({
          ...p,
          lastRankCheck: null, avgRankChange: 0,
          seoKpis: { tracked: 0, improved: 0, worsened: 0, same: 0, top20: 0, top10: 0, top3: 0, newTop20: 0, bestMover: null, improvingList: [] }
        }));
        setHubProducts(demo);
        return;
      }

      try {
        const enriched = await loadSeoSummariesForProducts(currentUser.uid, products);
        setHubProducts(enriched);
      } catch (e) {
        console.error('Error enriching products for Hub:', e);
        const fallback = products.map(p => ({
          ...p,
          lastRankCheck: null, avgRankChange: 0,
          seoKpis: { tracked: 0, improved: 0, worsened: 0, same: 0, top20: 0, top10: 0, top3: 0, newTop20: 0, bestMover: null, improvingList: [] }
        }));
        setHubProducts(fallback);
      }
    };
    hydrate();
  }, [currentUser, userTptProducts]);

  useEffect(() => {
    if (activeTab !== 'socialMediaMarketing') {
      setPreselectedSocialProduct(null);
    }
  }, [activeTab]);

  const triggerConfirmation = (message, onConfirm) => {
    setConfirmModalMessage(message);
    setOnConfirmAction(() => () => {
      onConfirm();
      setShowConfirmModal(false);
    });
    setShowConfirmModal(true);
  };

  const handleSelectTptProductForRewrite = (product) => {
    const normalized = {
      ...product,
      productTitle: product.productTitle || product.title || '',
      rewrittenDescription:
        product.rewrittenDescription ||
        product.descriptionHtml ||
        product.descriptionText ||
        product.description ||
        '',
      originalDescription:
        product.originalDescription ||
        product.descriptionHtml ||
        product.descriptionText ||
        product.description ||
        '',
    };
    setSelectedTptProductForRewrite(normalized);
    setShowRewriteSection(true);
    setDescSubTab('existing');
  };

  const handleProductSaveComplete = async (productId, updatedData) => {
    setUserTptProducts((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, ...updatedData, lastUpdated: new Date() } : p))
    );
    setSelectedTptProductForRewrite((prev) =>
      prev && prev.id === productId ? { ...prev, ...updatedData, lastUpdated: new Date() } : prev
    );
    try {
      if (currentUser && productId) {
        const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);
        await updateDoc(productRef, { lastUpdated: serverTimestamp() });
      }
    } catch (e) {
      console.error('Failed to write lastUpdated timestamp:', e);
      alert(`Error saving product: ${e.message}`);
    }
  };

  // --- START: CORRECTED FUNCTION (Line 332) ---
  const handleDeleteTptProduct = (productId, confirm = true) => {
    // 1. Define the core delete logic
    const deleteLogic = async () => {
      if (!currentUser) return;
      try {
        // RETURN the promise from deleteDoc
        return await deleteDoc(doc(db, 'userProducts', currentUser.uid, 'products', productId));
      } catch (e) {
        console.error('Error deleting product:', e);
        // Don't alert here, let the caller handle it
        throw e; // Re-throw so Promise.all (in bulk delete) can catch it
      }
    };

    // 2. Check which mode we're in
    if (confirm) {
      // Mode 1: Single delete. Show confirmation.
      triggerConfirmation('Are you sure you want to delete this product? This cannot be undone.', async () => {
        try {
          await deleteLogic();

          // Run these UI-only updates *after* the delete, only in single-confirm mode
          if (selectedTptProductForRewrite?.id === productId) {
            setSelectedTptProductForRewrite(null);
          }
          if (analytics) {
            logEvent(analytics, 'delete_tpt_product', { product_id: productId });
          }
        } catch (e) {
          // The deleteLogic function already logged, just alert
          alert(`Error deleting product: ${e.message}`);
        }
      });
      // We don't return anything here, it's fire-and-forget
    } else {
      // Mode 2: Bulk delete. NO confirmation. Return the promise.
      return deleteLogic();
    }
  };
  // --- END: CORRECTED FUNCTION ---


  const handleUpdatePins = async (productId, newPins) => {
    if (!currentUser || !productId) return;
    const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);
    try {
      await updateDoc(productRef, { pinnedKeywords: newPins });
    } catch (error) {
      console.error("Error updating pinned keywords: ", error);
      alert(`Error updating pins: ${error.message}`);
    }
  };

  const handleUpdateCustomTags = async (productId, updatedTags) => {
    if (!currentUser || !productId) {
      console.error("User not logged in or product ID missing");
      triggerAuthModal?.('Please log in to manage tags.');
      return;
    }
    const tagsToSave = Array.isArray(updatedTags) ? updatedTags : [];
    const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);
    try {
      await updateDoc(productRef, { customTags: tagsToSave });
      console.log(`Custom tags updated for product ${productId}`);
      if (analytics) {
        logEvent(analytics, 'hub_tag_update', { tag_type: 'custom', product_id: productId });
      }
    } catch (error) {
      console.error("Error updating custom tags: ", error);
      alert(`Failed to update custom tags: ${error.message}`);
    }
  };

  const handleUpdateSeasonalTags = async (productId, seasonalTags) => {
    if (!currentUser || !productId) {
      console.error("User not logged in or product ID missing");
      triggerAuthModal?.('Please log in to manage tags.');
      return;
    }
    const toSave = Array.isArray(seasonalTags) ? seasonalTags : [];
    const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);
    try {
      await updateDoc(productRef, { seasonalTags: toSave });
      console.log(`Seasonal tags updated for product ${productId}`);
      if (analytics) {
        logEvent(analytics, 'hub_tag_update', { tag_type: 'seasonal', product_id: productId });
      }
    } catch (error) {
      console.error("Error updating seasonalTags: ", error);
      alert(`Failed to update seasonal tags: ${error.message}`);
    }
  };

  const handleUpdateSubjectTags = async (productId, subjectTags) => {
    if (!currentUser || !productId) return triggerAuthModal?.('Please log in.');
    const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);
    try {
      await updateDoc(productRef, { subjectTags: Array.isArray(subjectTags) ? subjectTags : [] });
      if (analytics) {
        logEvent(analytics, 'hub_tag_update', { tag_type: 'subject', product_id: productId });
      }
    } catch(e) {
      console.error("Error updating subject tags: ", e);
      alert(`Failed to update subject tags: ${e.message}`);
    }
  };

  const handleUpdateGradeTags = async (productId, gradeTags) => {
    if (!currentUser || !productId) return triggerAuthModal?.('Please log in.');
    const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);
    try {
      await updateDoc(productRef, { gradeTags: Array.isArray(gradeTags) ? gradeTags : [] });
      if (analytics) {
        logEvent(analytics, 'hub_tag_update', { tag_type: 'grade', product_id: productId });
      }
    } catch(e) {
      console.error("Error updating grade tags: ", e);
      alert(`Failed to update grade tags: ${e.message}`);
    }
  };

  const handleUpdateResourceTypes = async (productId, resourceTypeTags) => {
    if (!currentUser || !productId) return triggerAuthModal?.('Please log in.');
    const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);
    try {
      await updateDoc(productRef, { resourceTypeTags: Array.isArray(resourceTypeTags) ? resourceTypeTags : [] });
      if (analytics) {
        logEvent(analytics, 'hub_tag_update', { tag_type: 'resource_type', product_id: productId });
      }
    } catch(e) {
      console.error("Error updating resource tags: ", e);
      alert(`Failed to update resource tags: ${e.message}`);
    }
  };

  const handleMarkProductUpdated = async (productId) => {
    if (!currentUser || !productId) return triggerAuthModal?.('Please log in.');
    const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);
    try {
      await updateDoc(productRef, { lastUpdated: serverTimestamp() });
    } catch(e) {
      console.error("Error marking as updated: ", e);
      alert(`Failed to mark as updated: ${e.message}`);
    }
  };

  const handleMarkUploadedToTpt = async (productId) => {
    if (!currentUser || !productId) return triggerAuthModal?.('Please log in.');
    const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);
    try {
      await updateDoc(productRef, { tptUploadedAt: serverTimestamp() });
    } catch(e) {
      console.error("Error marking as uploaded: ", e);
      alert(`Failed to mark as uploaded: ${e.message}`);
    }
  };

  // Drawer sync helpers
  const getProductById = useCallback((id) => {
    return hubProducts.find(p => p.id === id) || userTptProducts.find(p => p.id === id) || null;
  }, [hubProducts, userTptProducts]);

  const recomputeAuditForProduct = useCallback(async (p) => {
    const fresh = getProductById(p.id) || p;
    const newAudit = runAudit(fresh, fresh.auditOverrides || {});
    setHubProducts(prev => prev.map(x => x.id === fresh.id ? { ...x, audit: newAudit } : x));
    return newAudit;
  }, [getProductById]);

  const handleUpdateAuditOverrides = async (productId, overridesOrPatch) => {
    if (!currentUser || !productId) {
      console.error("User not logged in or product ID missing for override save.");
      triggerAuthModal?.('Please log in.');
      return;
    }

    const current = (getProductById(productId)?.auditOverrides) || {};
    let nextOverrides = current;

    if (overridesOrPatch && typeof overridesOrPatch === 'object') {
      const keys = Object.keys(overridesOrPatch);
      if (keys.length === 1 && typeof overridesOrPatch[keys[0]] === 'boolean') {
        const [k, v] = Object.entries(overridesOrPatch)[0];
        nextOverrides = v ? { ...current, [k]: true } : (() => { const { [k]:_, ...rest } = current; return rest; })();
      } else {
        nextOverrides = { ...overridesOrPatch };
      }
    }

    const productRef = doc(db, 'userProducts', currentUser.uid, 'products', productId);

    try {
      await updateDoc(productRef, {
        auditOverrides: nextOverrides,
        auditOverrideUpdated: serverTimestamp()
      });

      setUserTptProducts(prevProducts =>
        prevProducts.map(p =>
          p.id === productId
            ? { ...p, auditOverrides: nextOverrides, auditOverrideUpdated: new Date() }
            : p
        )
      );

      setHubProducts(prevProducts =>
        prevProducts.map(p =>
          p.id === productId
            ? { ...p, auditOverrides: nextOverrides, auditOverrideUpdated: new Date() }
            : p
        )
      );

      console.log(`[Success] Audit overrides saved and local state patched for ${productId}`);
    } catch(e) {
      console.error("Error updating overrides in Firestore: ", e);
      alert(`Failed to save override setting: ${e.message}`);
    }
  };

  const handleLoadSavedDescription = (savedData) => {
    if (!savedData) return;
    triggerConfirmation(
      `Load snapshot "${savedData.saveName}"? This will overwrite current fields in the "Generate New Description" form.`,
      () => {
        setActiveTab('descriptionTools');
        setDescSubTab('new');
        setSnapshotToLoad(savedData);
        setProductName(savedData.productName || '');
        setSelectedTitle(savedData.productName || '');
        if (analytics) {
          logEvent(analytics, 'load_saved_description', { snapshot_id: savedData.id });
        }
        mainContentRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    );
  };

  const handleDeleteSavedDescription = (descriptionId, descriptionName) => {
    triggerConfirmation(
      `Are you sure you want to delete the snapshot "${descriptionName}"? This action cannot be undone.`,
      async () => {
        if (!currentUser) return;
        try {
          await deleteDoc(doc(db, 'users', currentUser.uid, 'savedDescriptions', descriptionId));
          if (analytics) {
            logEvent(analytics, 'delete_saved_description', { snapshot_id: descriptionId });
          }
        } catch(e) {
          console.error("Error deleting snapshot: ", e);
          alert(`Failed to delete snapshot: ${e.message}`);
        }
      }
    );
  };

  const handleFullReset = () => {
    triggerConfirmation('Are you sure you want to reset all forms and generated content?', () => {
      window.location.reload();
    });
  };

  const handleTitleSelection = (title) => {
    setSelectedTitle(title);
    setProductName(title);
  };

  const handleBulkImport = async (storeUrl) => {
    if (!currentUser) {
      triggerAuthModal('Please log in to import your store products.');
      return;
    }
    setIsImporting(true);
    setImportError('');
    try {
      const importTptStore = httpsCallable(functionsInstance, 'importTptStore');
      const result = await importTptStore({ storeUrl });
      if (result.data.success) {
        alert(`Import in progress! Please allow a short moment for your TPT products to populate under the "My TPT Product" side menu. `);
        setIsBulkImportModalOpen(false);
      } else {
        throw new Error(result.data.error || 'An unknown error occurred during import.');
      }
    } catch (err) {
      console.error('Error calling importTptStore function:', err);
      setImportError(err.message || 'An unexpected error occurred. Please check the URL and try again.');
    } finally {
      setIsImporting(false);
    }
  };

  // Quick nav helpers
  const handleGoToRewrite = (product) => {
    setActiveTab('descriptionTools');
    setDescSubTab('existing');
    setTimeout(() => {
      handleSelectTptProductForRewrite(product);
    }, 100);
  };

  const handleGoToSeo = (product) => {
    if (!product) return;
    setPreselectedSeoProductId(product.id);
    setPreselectedKeywordForGraph(null);
    setActiveTab('seoRankTracker');
  };

  const handleGoToSeoAndSelectKeyword = (product, keyword) => {
    if (!product || !keyword) return;
    setPreselectedSeoProductId(product.id);
    setPreselectedKeywordForGraph(keyword);
    setActiveTab('seoRankTracker');
  };

  const handleGoToSocial = (product) => {
    if (!product) return;
    setPreselectedSocialProduct(product);
    setActiveTab('socialMediaMarketing');
  };

  const handleAuditQuickFix = (key, product) => {
    const prod = product || selectedTptProductForRewrite || null;
    switch (key) {
      case 'intro':
      case 'readability':
      case 'bullets':
      case 'cta':
      case 'backlinks':
      case 'standards':
      case 'grades':
        setActiveTab('descriptionTools');
        setDescSubTab('existing');
        if (prod) handleSelectTptProductForRewrite(prod);
        break;
      case 'title':
        setActiveTab('descriptionTools');
        setDescSubTab('titles');
        break;
      default:
        break;
    }
  };

  const handleReanalyzeAll = async () => {
    if (!currentUser) {
      triggerAuthModal('Please log in to run a full analysis.');
      return;
    }
    setIsReanalyzingAll(true);
    try {
      const fn = httpsCallable(functionsInstance, 'runManualAuditForAllProducts');
      await fn();
      if (analytics) logEvent(analytics, 'hub_reanalyze_all_clicked');
      alert("Analysis for all products has started. The hub will update with new scores as they are completed.");
    } catch (e) {
      console.error('runManualAuditForAllProducts failed:', e);
      alert('Something went wrong while starting the full analysis. Please try again.');
    } finally {
      setIsReanalyzingAll(false);
    }
  };

  // --- Open Coach Panel for a specific product and SCROLL/FOCUS it ---
  const openCoachAndFocus = useCallback((product) => {
    if (!product) return;
    const id = product.id;

    // 1) set focus id for the Hub (if it supports it)
    setPreselectedCoachProductId(id);

    // 2) go to the Hub tab
    setActiveTab('productImprovementHub');

    // 3) broadcast an event the Hub can listen for to auto-expand the item
    try {
      window.dispatchEvent(new CustomEvent('openCoachForProduct', { detail: { productId: id } }));
    } catch {}

  }, []);
  // --------------------------------------------------------------------

  // Loading gate
  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-100">
        <Loader2 className="h-12 w-12 animate-spin text-sky-600" />
        <p className="ml-4 text-lg font-semibold text-slate-700">Loading Application...</p>
      </div>
    );
  }

  // Access control
  let hasFullAccess = false;
  let isTrialActive = false;
  let trialTimeRemaining = '';

  if (currentUser && userAccessData) {
    if (userAccessData.approvedByAdmin) {
      hasFullAccess = true;
    } else if (userAccessData.accessStatus === 'trial') {
      if (userAccessData.trialEndsAt && typeof userAccessData.trialEndsAt.toDate === 'function') {
        if (userAccessData.trialEndsAt.toDate() > new Date()) {
          hasFullAccess = true;
          isTrialActive = true;
          const remaining = userAccessData.trialEndsAt.toDate().getTime() - new Date().getTime();
          const hours = Math.floor(remaining / (1000 * 60 * 60));
          const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
          trialTimeRemaining = `Approx. ${hours}h ${minutes}m remaining.`;
        }
      }
    }
  }

  if (currentUser && !hasFullAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-100 to-indigo-100 p-4 text-center">
        <Edit3 className="h-16 w-16 text-amber-500 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-sky-700 mb-3">Mr Joel's TPT SEO Tool</h1>
        <p className="text-slate-700 mb-2">Logged in as: {currentUser.email}</p>
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-lg w-full">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-3">Access Expired or Pending</h2>
          <p className="text-slate-600 mb-4">
            Your trial may have expired or access is pending. Please purchase full access or contact support if you've already purchased using the email below.
          </p>
          <a href={TPT_PURCHASE_LINK} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-800 underline font-medium">
            Purchase on TPT
          </a>
          <p className="text-xs text-slate-500 my-4">
            If you have issues, please contact <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600">{SUPPORT_EMAIL}</a>.
          </p>
          <button onClick={handleLogout} className="bg-sky-600 hover:bg-sky-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md flex items-center justify-center mx-auto">
            <LogOut size={18} className="mr-2" /> Logout
          </button>
        </div>
        <Disclaimer className="mt-8" />
      </div>
    );
  }

  // --- This is your entire app, now returned as a component ---
  return (
    <AuditDrawerProvider
      onQuickFix={handleAuditQuickFix}
      onLoadProduct={handleSelectTptProductForRewrite}
      onRerunAuditForProduct={recomputeAuditForProduct}
      onUpdateAuditOverrides={handleUpdateAuditOverrides}
      getProductById={getProductById}
      onOpenCoachPanel={openCoachAndFocus}
    >
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-sky-100 font-sans">
        {/* Pass the visibility setter to NotificationManager */}
        <NotificationManager onVisibilityChange={setIsSystemNotificationOpen} />

        <Toast message="🔄 Syncing your latest keyword ranks..." show={showSyncToast} />
        <ConfirmationModal
          isOpen={showConfirmModal}
          message={confirmModalMessage}
          onConfirm={onConfirmAction}
          onCancel={() => setShowConfirmModal(false)}
        />
        {authHook.showAuthModal && (
          <AuthModal
            {...authHook}
            handleGoogleSignIn={handleGoogleSignIn}
            onClose={() => authHook.setShowAuthModal(false)}
          />
        )}
        <BulkImportModal
          isOpen={isBulkImportModalOpen}
          onClose={() => setIsBulkImportModalOpen(false)}
          onImport={handleBulkImport}
          isTrial={isTrialActive && !userAccessData?.approvedByAdmin}
          isImporting={isImporting}
          error={importError}
        />

        {/* Pass the visibility state to WalkthroughTour */}
        <WalkthroughTour
          currentUser={currentUser}
          isTrialActive={isTrialActive}
          hasActiveNotification={isSystemNotificationOpen}
        />

        <Sidebar
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
          currentUser={currentUser}
          userAccessData={userAccessData}
          isTrialActive={isTrialActive}
          trialTimeRemaining={trialTimeRemaining}
          handleLogout={handleLogout}
          triggerAuthModal={triggerAuthModal}
          userTptProducts={hubProducts}
          savedDescriptions={savedDescriptions}
          handleSelectTptProductForRewrite={handleSelectTptProductForRewrite}
          handleLoadSavedDescription={handleLoadSavedDescription}
          handleDeleteTptProduct={handleDeleteTptProduct}
          handleDeleteSavedDescription={handleDeleteSavedDescription}
          onBulkImportClick={() => setIsBulkImportModalOpen(true)}
          onGoToSeo={handleGoToSeo}
          onGoToSocial={handleGoToSocial}
        />

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto" ref={mainContentRef}>
          <h1 className="sr-only">TPT Seller SEO & Marketing Dashboard</h1>
          <Header
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            currentUser={currentUser}
            isTrialActive={isTrialActive}
            userAccessData={userAccessData}
            trialTimeRemaining={trialTimeRemaining}
            handleLogout={handleLogout}
            handleFullReset={handleFullReset}
            triggerAuthModal={triggerAuthModal}
          />

          <div className="mb-6 border-b border-slate-200 flex justify-center">
            <nav className="flex flex-col sm:flex-row flex-wrap sm:space-x-1 rounded-lg p-1 bg-slate-200" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('descriptionTools')}
                className={`w-full sm:w-auto px-3 py-2 text-xs sm:text-sm font-semibold rounded-md flex items-center justify-center sm:justify-start whitespace-nowrap ${activeTab === 'descriptionTools' ? 'bg-white shadow text-sky-700' : 'text-slate-600 hover:bg-slate-300'}`}
              >
                <FileText size={14} className="mr-1 sm:mr-2" /> Description Tools
              </button>
              <button
                onClick={() => setActiveTab('seoRankTracker')}
                className={`w-full sm:w-auto px-3 py-2 text-xs sm:text-sm font-semibold rounded-md flex items-center justify-center sm:justify-start whitespace-nowrap ${activeTab === 'seoRankTracker' ? 'bg-white shadow text-sky-700' : 'text-slate-600 hover:bg-slate-300'}`}
              >
                <BarChart3 size={14} className="mr-1 sm:mr-2" /> SEO & Rank Tracker
              </button>
              {ENABLE_KEYWORD_RESEARCH && (
                <button
                    onClick={() => setActiveTab('keywordResearch')}
                    className={`w-full sm:w-auto px-3 py-2 text-xs sm:text-sm font-semibold rounded-md flex items-center justify-center sm:justify-start whitespace-nowrap ${activeTab === 'keywordResearch' ? 'bg-white shadow text-sky-700' : 'text-slate-600 hover:bg-slate-300'}`}
                >
                    <Layers size={14} className="mr-1 sm:mr-2" /> Keyword Research
                </button>
              )}
              <button
                onClick={() => setActiveTab('socialMediaMarketing')}
                className={`w-full sm:w-auto px-3 py-2 text-xs sm:text-sm font-semibold rounded-md flex items-center justify-center sm:justify-start whitespace-nowrap ${activeTab === 'socialMediaMarketing' ? 'bg-white shadow text-sky-700' : 'text-slate-600 hover:bg-slate-300'}`}
              >
                <Share2 size={14} className="mr-1 sm:mr-2" /> Social Media Marketing
              </button>
              {ENABLE_IMPROVEMENT_HUB && (
                <button
                  onClick={() => {
                    setActiveTab('productImprovementHub');
                    if (analytics) {
                        logEvent(analytics, 'view_improvement_hub');
                    }
                  }}
                  className={`w-full sm:w-auto px-3 py-2 text-xs sm:text-sm font-semibold rounded-md flex items-center justify-center sm:justify-start whitespace-nowrap ${activeTab === 'productImprovementHub' ? 'bg-white shadow text-sky-700' : 'text-slate-600 hover:bg-slate-300'}`}
                >
                  <BrainCircuit size={14} className="mr-1 sm:mr-2" /> Improvement Hub
                </button>
              )}
              {/* --- NEW: Bundles tab button --- */}
              <button
                onClick={() => setActiveTab('bundles')}
                className={`w-full sm:w-auto px-3 py-2 text-xs sm:text-sm font-semibold rounded-md flex items-center justify-center sm:justify-start whitespace-nowrap ${activeTab === 'bundles' ? 'bg-white shadow text-sky-700' : 'text-slate-600 hover:bg-slate-300'}`}
              >
                <Layers size={14} className="mr-1 sm:mr-2" /> Bundles (Beta)
              </button>
            </nav>
          </div>

          <Disclaimer className="mb-8" />

          {activeTab === 'descriptionTools' && (
            <>
              <AddTptProductForm
                currentUser={currentUser}
                userTptProducts={userTptProducts}
                onProductSelect={handleSelectTptProductForRewrite}
                triggerAuthModal={triggerAuthModal}
              />

              {/* Inner tab panel */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mt-6">

                {/* Inner tab headers */}
                <div className="flex border-b border-slate-200">
                  {[
                    { id: 'existing', label: 'Improve Existing', icon: <RefreshCw size={15} /> },
                    { id: 'titles', label: 'Title Generator', icon: <Sparkles size={15} /> },
                    { id: 'new', label: 'New Description', icon: <PenLine size={15} /> },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setDescSubTab(tab.id)}
                      className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                        descSubTab === tab.id
                          ? 'border-sky-600 text-sky-700'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>

                {/* Improve Existing tab */}
                {descSubTab === 'existing' && (
                  <div className="p-6">
                    <RewriteDescriptionForm
                      currentUser={currentUser}
                      userTptProducts={userTptProducts}
                      selectedTptProductForRewrite={selectedTptProductForRewrite}
                      triggerAuthModal={triggerAuthModal}
                      onSaveComplete={handleProductSaveComplete}
                    />
                  </div>
                )}

                {/* Title Generator tab */}
                {descSubTab === 'titles' && (
                  <div className="p-6">
                    <TitleGenerator
                      currentUser={currentUser}
                      triggerAuthModal={triggerAuthModal}
                      formSnapshot={{ ...formSnapshot, productName }}
                      selectedTitle={selectedTitle}
                      onTitleSelect={handleTitleSelection}
                    />
                  </div>
                )}

                {/* New Description tab */}
                {descSubTab === 'new' && (
                  <div className="p-6 space-y-6">
                    <TitleGenerator
                      currentUser={currentUser}
                      triggerAuthModal={triggerAuthModal}
                      formSnapshot={{ ...formSnapshot, productName }}
                      selectedTitle={selectedTitle}
                      onTitleSelect={handleTitleSelection}
                    />
                    <NewDescriptionForm
                      currentUser={currentUser}
                      triggerAuthModal={triggerAuthModal}
                      onDescriptionGenerated={setGeneratedDescription}
                      onFormStateChange={setFormSnapshot}
                      userTptProducts={userTptProducts}
                      snapshotToLoad={snapshotToLoad}
                      productName={productName}
                      setProductName={setProductName}
                    />
                    <GeneratedDescription
                      currentUser={currentUser}
                      triggerAuthModal={triggerAuthModal}
                      generatedDescription={generatedDescription}
                      setGeneratedDescription={setGeneratedDescription}
                      formSnapshot={{ ...formSnapshot, productName }}
                      showConfirmationModal={triggerConfirmation}
                      selectedTitle={selectedTitle}
                    />
                  </div>
                )}

              </div>
            </>
          )}

          {activeTab === 'seoRankTracker' && (
            <SeoRankTracker
              currentUser={currentUser}
              userTptProducts={userTptProducts}
              triggerAuthModal={triggerAuthModal}
              onGoToRewrite={handleGoToRewrite}
              keywordLimit={userAccessData?.keywordLimit || 5}
              preselectedProductId={preselectedSeoProductId}
              onClearPreselect={() => setPreselectedSeoProductId(null)}
              preselectedKeyword={preselectedKeywordForGraph}
              onClearPreselectKeyword={() => setPreselectedKeywordForGraph(null)}
            />
          )}

          {ENABLE_KEYWORD_RESEARCH && activeTab === 'keywordResearch' && (
            <KeywordResearchTool
              currentUser={currentUser}
              userAccessData={userAccessData} // <--- PASSED HERE
              triggerAuthModal={triggerAuthModal}
            />
          )}

          {activeTab === 'socialMediaMarketing' && (
            <SocialMediaPostGenerator
              currentUser={currentUser}
              userTptProducts={userTptProducts}
              initialProduct={preselectedSocialProduct}
            />
          )}

          {ENABLE_IMPROVEMENT_HUB && activeTab === 'productImprovementHub' && (
            <ProductImprovementHub
              hubProducts={hubProducts}
              currentUser={currentUser}
              userTptProducts={userTptProducts}
              onQuickFix={handleAuditQuickFix}
              onGoToRewrite={handleGoToRewrite}
              onGoToSeoTracker={handleGoToSeo}
              onReanalyzeAll={handleReanalyzeAll}
              onUpdatePins={handleUpdatePins}
              isReanalyzingAll={isReanalyzingAll}
              isDemoMode={!currentUser}
              triggerAuthModal={triggerAuthModal}
              onGoToSeoAndSelectKeyword={handleGoToSeoAndSelectKeyword}
              onUpdateCustomTags={handleUpdateCustomTags}
              onUpdateSeasonalTags={handleUpdateSeasonalTags}
              onUpdateSubjectTags={handleUpdateSubjectTags}
              onUpdateGradeTags={handleUpdateGradeTags}
              onUpdateResourceTypes={handleUpdateResourceTypes}
              onMarkProductUpdated={handleMarkProductUpdated}
              onMarkUploadedToTpt={handleMarkUploadedToTpt}
              onUpdateAuditOverrides={handleUpdateAuditOverrides}
              focusProductId={preselectedCoachProductId}
              onClearFocus={() => setPreselectedCoachProductId(null)}
              renderInlineRankGraph={(product, keyword) => (
                <div className="text-xs text-slate-600">
                  Showing trend for <b>{keyword}</b>. Open full SEO view for deeper analysis.
                </div>
              )}
            />
          )}

          {/* --- NEW: Bundles panel render --- */}
          {activeTab === 'bundles' && (
            <BundleComposer
              products={userTptProducts}               // or hubProducts if you prefer the enriched set
              currentUser={currentUser}
              tone={formSnapshot?.tone || 'Clear & Professional'}
              audience={formSnapshot?.audience || 'teachers'}
              subject={formSnapshot?.subject || ''}
              gradeBand={formSnapshot?.gradeBand || ''}
              descriptionLength={formSnapshot?.descriptionLength || 'medium'}
              generateBundleCopy={generateBundleCopy}
              onSaved={(docId) => console.log('Bundle plan saved:', docId)}
            />
          )}
        </main>
      </div>
    </AuditDrawerProvider>
  );
};


// --- THIS IS THE MAIN APP COMPONENT ---
// It just passes the auth hook to the AppContent
// NO ROUTER LOGIC HERE
const App = () => {
  const authHook = useAuth();
  return <AppContent authHook={authHook} />;
};

export default App; 